import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { confirmEmailThunk } from '../redux/userSlice'
import { useCollaborations } from '../hooks/useCollaborations'
import { AppDispatch } from '../store'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { CheckCircle, XCircle } from 'lucide-react'

const useAppDispatch = () => useDispatch<AppDispatch>()

const EmailConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { refetch: refreshCampaigns } = useCollaborations()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying')
  const [message, setMessage] = useState('Verifying your email address...')
  const hasVerified = React.useRef(false)

  useEffect(() => {
    const token = searchParams.get('token')

    if (!token) {
      setStatus('error')
      setMessage('No confirmation token found. Please check the link or try again.')
      return
    }

    if (hasVerified.current) {
      return
    }
    hasVerified.current = true

    const verifyEmail = async () => {
      try {
        await dispatch(confirmEmailThunk(token)).unwrap()
        setStatus('success')
        setMessage('Your email has been successfully verified! Setting up your profile...')

        // Refresh campaigns data
        refreshCampaigns()

        // Redirect to pending invite if one was stored, otherwise dashboard
        // (ProtectedRoute sends a new athlete to the welcome first)
        const pendingMarketerInvite = localStorage.getItem('pendingMarketerInviteToken')
        if (pendingMarketerInvite) {
          localStorage.removeItem('pendingMarketerInviteToken')
          setTimeout(() => navigate(`/marketer-invite/${pendingMarketerInvite}`), 2000)
        } else {
          setTimeout(() => navigate('/'), 2000)
        }
      } catch (err: any) {
        setStatus('error')
        setMessage(err || 'An error occurred during email verification.')
      }
    }

    verifyEmail()
  }, [searchParams, dispatch, navigate, refreshCampaigns])

  return (
    <div className="flex items-center justify-center min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-background"></div>

      <div className="w-full max-w-md px-4">
        <Card variant="dark">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {status === 'verifying' && (
                <img
                  src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                  alt="Athios Logo"
                  className="w-10 h-10 animate-pulse"
                />
              )}
              {status === 'success' && <CheckCircle className="w-16 h-16 text-green-600" />}
              {status === 'error' && <XCircle className="w-16 h-16 text-red-600" />}
            </div>
            <CardTitle className="text-3xl font-semibold text-foreground tracking-tight">
              {status === 'verifying' && 'Verifying Email'}
              {status === 'success' && 'Welcome to Athios!'}
              {status === 'error' && 'Verification Failed'}
            </CardTitle>
            <CardDescription className="mt-2 text-gray-600">{message}</CardDescription>
          </CardHeader>
          {status === 'error' && (
            <CardContent>
              <Button
                onClick={() => navigate('/signin')}
                variant="lime"
                className="w-full font-medium"
              >
                Go to Sign In
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  )
}

export default EmailConfirmation
